import {
  ITourFormSettings, ITranslations,
} from '@llws/lift-entity-interfaces';
import { IQueryCommandOptions, convertQueryCommandToQueryString, Equal } from '@llws/dynamic-router-utils';
import vm from '@/main';
import {
  IApiResponse,
  IMetaResponse,
} from '@/shared';
import { StoreBaseImplementationV2 } from '@/shared/classes/store-base-implementation-v2';
import { IAction, IStateStrict } from '@/shared/interfaces/store-base';
import { adminRequest } from '@/shared/request-configs/admin-request';

import { ITourFormSettingsWithCorrectFields } from '@/shared/interfaces/tour-form-settings';
import { getTranslationsByClientIdModelKeyAndLanguageId, saveTranslations } from '../../shared/services/translations';

interface ITourFormSettingsState {
  tourFormSettings: ITourFormSettingsWithCorrectFields,
  propertyTourFormSettings: ITourFormSettingsWithCorrectFields,
}

// Base store configuration
const agentGatewayResourcePathAgent: string = '/tour-api-agent';
const adminGatewayResourcePathAdmin: string = '/tour-api-admin';
const listViewName: string = '';
const showLogging: boolean = false;

const defaultGetListFilters: IQueryCommandOptions = {};

const defaultGetSingleFilters: IQueryCommandOptions = {};

// Instantiate the base store with the params
const base = new StoreBaseImplementationV2<ITourFormSettings>({
  adminGatewayResourcePath: adminGatewayResourcePathAdmin,
  defaultGetSingleFilters,
  defaultGetListFilters,
  listViewName,
  showLogging,
});

export default {
  namespaced: base.namespaced,

  state: {
    ...base.state,
    tourFormSettings: undefined as ITourFormSettings | undefined,
    propertyTourFormSettings: undefined as ITourFormSettings | undefined,
  },

  getters: {
    ...base.getters,
    tourFormSettings(state: ITourFormSettingsState) { return state.tourFormSettings; },
    propertyTourFormSettings(state: ITourFormSettingsState) { return state.propertyTourFormSettings; },
  },

  mutations: {
    ...base.mutations,
    setTourFormSettings(state: ITourFormSettingsState, payload: ITourFormSettingsWithCorrectFields) {
      state.tourFormSettings = payload;
    },
    setPropertyTourFormSettings(state: ITourFormSettingsState, payload: ITourFormSettingsWithCorrectFields) {
      state.propertyTourFormSettings = payload;
    },

  },

  actions: {
    ...base.actions,
    getTourFormSettings: async (ctx: IAction<IStateStrict<ITourFormSettings>>, clientId?: number) => {
      const response = await adminRequest.get(`${vm()?.$rsGlobals.$hasToursRestricted() ? agentGatewayResourcePathAgent : adminGatewayResourcePathAdmin}/tour_form_settings${clientId ? `?where=clientId:${clientId}` : ''}`);
      if (response.data.length > 0) {
        ctx.commit('setTourFormSettings', response.data[0]);
        return response.data[0];
      }
      return undefined;
    },
    getDefaultTourFormSettings: async (): Promise<ITourFormSettings> => {
      const response = await adminRequest.get(`${adminGatewayResourcePathAdmin}/tour-form-default`) as ITourFormSettings;
      return response;
    },
    async saveTourFormSettings(ctx: IAction<IStateStrict<ITourFormSettings>>, payload: ITourFormSettings) {
      if (payload.id) {
        const response = await adminRequest.put(`${adminGatewayResourcePathAdmin}/tour_form_settings/${payload.id!}`, payload);
        if (response) ctx.commit('setTourFormSettings', response.data);
      } else {
        const response = await adminRequest.post(`${adminGatewayResourcePathAdmin}/tour_form_settings`, payload);
        if (response?.data.length > 0) {
          ctx.commit('setTourFormSettings', response.data[0]);
        }
      }
    },
    getPropertyTourFormSettings: async (ctx: IAction<IStateStrict<ITourFormSettings>>, payload: {propertyId: number;}):
    Promise<IApiResponse<ITourFormSettings, null>> => {
      const response = await adminRequest.get(vm()?.$rsGlobals.$hasToursRestricted()
        ? `${agentGatewayResourcePathAgent}/${payload.propertyId}/property_tour_form_settings`
        : `${adminGatewayResourcePathAdmin}/${payload.propertyId}/property_tour_form_settings`);
      if (response) ctx.commit('setPropertyTourFormSettings', response);
      return response;
    },

    /**
     * Get tour form settings with translations
     * @param ctx
     * @param foreignId
     * @returns
     */
    getTourFormSettingsTranslations: async (
      ctx: IAction<IStateStrict<ITourFormSettings>>,
      foreignId: number | null | undefined,
    ) => {
      const query: IQueryCommandOptions = {
        where: [
          Equal('model', 'tour_form_settings'),
          Equal('foreignId', foreignId),
        ],
        limit: 9999,
      };

      const response: IApiResponse<ITranslations[], IMetaResponse> = await adminRequest.get(
        `/translations-api/translations${convertQueryCommandToQueryString(query)}`,
      );

      return response.data ?? [];
    },

    async saveTextMessageTranslations(ctx: IAction<IStateStrict<ITourFormSettings>>, payload: {
      translationTextMessage: string, clientId: number, foreignId: number | null | undefined
    }) {
      const translatedTextMessages = await getTranslationsByClientIdModelKeyAndLanguageId(payload.clientId, 'tour-form-settings', 1, 'text_message');

      if (translatedTextMessages.length > 0) {
        translatedTextMessages[0].value = payload.translationTextMessage;
        await saveTranslations(translatedTextMessages);
        return;
      }
      await saveTranslations([
        {
          clientId: payload.clientId,
          model: 'tour-form-settings',
          key: 'text_message',
          value: payload.translationTextMessage,
          languageId: 1,
          foreignId: payload.foreignId ?? 0,
        },
      ]);
    },
  },
};
